<template>
	<div class="spinner-container">
		<div v-if="name === 'crescent'" class="lds-ring" :class="[size, color]">
			<div></div>
			<div></div>
			<div></div>
			<div></div>
		</div>

		<div v-else-if="name === 'dots'" class="lds-dots" :class="[size, color]">
			<div></div>
			<div></div>
			<div></div>
		</div>
	</div>
</template>

<script lang="ts" setup>
withDefaults(
	defineProps<{
		size?: 'sm' | 'md' | 'lg';
		color?: 'gold' | 'light';
		name?: 'crescent' | 'dots';
	}>(),
	{
		size: 'md',
		color: 'light',
		name: 'crescent',
	}
);
</script>

<style lang="scss" scoped>
.spinner-container {
	--spinner-size-sm: 28px;
	--spinner-border-sm: 3px;
	--spinner-size-md: 56px;
	--spinner-border-md: 5px;
	--spinner-size-lg: 84px;
	--spinner-border-lg: 6px;
	--spinner-color-light: #fff;
	--spinner-color-gold: #fbc500;
	--dot-size-sm: 3px;
	--dot-gap-sm: 2px;
	--dot-size-md: 6px;
	--dot-gap-md: 4px;
	--dot-size-lg: 10px;
	--dot-gap-lg: 8px;

	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
}

.lds-ring {
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	pointer-events: none;

	&.sm {
		height: unset;
		width: unset;

		div {
			width: var(--spinner-size-sm);
			height: var(--spinner-size-sm);
			margin: var(--spinner-border-sm);
			border-width: var(--spinner-border-sm);
		}
	}

	&.md {
		height: unset;
		width: unset;

		div {
			width: var(--spinner-size-md);
			height: var(--spinner-size-md);
			margin: var(--spinner-border-md);
			border-width: var(--spinner-border-md);
		}
	}

	&.lg {
		height: unset;
		width: unset;

		div {
			width: var(--spinner-size-lg);
			height: var(--spinner-size-lg);
			margin: var(--spinner-border-lg);
			border-width: var(--spinner-border-lg);
		}
	}

	&.light div {
		border-color: var(--spinner-color-light) transparent transparent transparent;
	}

	&.gold div {
		border-color: var(--spinner-color-gold) transparent transparent transparent;
	}
}

.lds-ring div {
	box-sizing: border-box;
	display: block;
	position: absolute;
	border-style: solid;
	border-radius: 50%;
	animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.lds-ring div:nth-child(1) {
	animation-delay: -0.45s;
}

.lds-ring div:nth-child(2) {
	animation-delay: -0.3s;
}

.lds-ring div:nth-child(3) {
	animation-delay: -0.15s;
}

@keyframes lds-ring {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.lds-dots {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: var(--dot-gap-md);
	position: relative;
	pointer-events: none;

	&.sm {
		gap: var(--dot-gap-sm);

		div {
			width: var(--dot-size-sm);
			height: var(--dot-size-sm);
		}
	}

	&.md {
		gap: var(--dot-gap-md);

		div {
			width: var(--dot-size-md);
			height: var(--dot-size-md);
		}
	}

	&.lg {
		gap: var(--dot-gap-lg);

		div {
			width: var(--dot-size-lg);
			height: var(--dot-size-lg);
		}
	}

	&.light div {
		background-color: var(--spinner-color-light);
	}

	&.gold div {
		background-color: var(--spinner-color-gold);
	}
}

.lds-dots div {
	border-radius: 50%;
	animation: lds-dots 1.4s ease-in-out infinite;
}

.lds-dots div:nth-child(1) {
	animation-delay: -0.32s;
}

.lds-dots div:nth-child(2) {
	animation-delay: -0.16s;
}

.lds-dots div:nth-child(3) {
	animation-delay: 0s;
}

@keyframes lds-dots {
	0%,
	80%,
	100% {
		opacity: 0.3;
		transform: scale(0.8);
	}
	40% {
		opacity: 1;
		transform: scale(1);
	}
}
</style>
